/**
 * Hierarchical Features Utility
 *
 * Parses and manages parent-child relationships in feature codes using the :: delimiter.
 * Format: childFeature::requiredParentFeature
 *
 * Example: "inspections_delete::inspections_view" means:
 *   - "inspections_delete" is the child feature
 *   - "inspections_view" is the required parent feature
 *   - The child can only be enabled if the parent is enabled
 *
 * Special pseudo-parent "!" (DEFAULT_UNCHECKED_MARKER):
 *   - Format: "featureCode::!"
 *   - Indicates the feature should be enabled but NOT checked by default
 *   - Used for independent features that users should opt-in to
 *   - Example: "evirdrafts::!" means View Draft Configurations is available but unchecked by default
 */

// Pseudo-parent marker for features that should be enabled but not checked by default
export const DEFAULT_UNCHECKED_MARKER = '!';

/**
 * Parses a feature code to extract parent dependency
 * @param {string} code - Feature code, possibly containing :: for parent dependency
 * @returns {{ featureCode: string, parentCode: string | null }} Parsed feature info
 */
export const parseFeatureCode = (code) => {
  if (!code || typeof code !== 'string') {
    return { featureCode: code || '', parentCode: null };
  }

  // Trim whitespace from the code to handle backend data issues
  const trimmedCode = code.trim();

  const delimiterIndex = trimmedCode.indexOf('::');
  if (delimiterIndex === -1) {
    return { featureCode: trimmedCode, parentCode: null };
  }

  const featureCode = trimmedCode.substring(0, delimiterIndex).trim();
  const parentCode = trimmedCode.substring(delimiterIndex + 2).trim();

  return {
    featureCode: featureCode || trimmedCode,
    parentCode: parentCode || null,
  };
};

/**
 * Gets the display name for a feature (without the ::parent suffix)
 * @param {string} code - Feature code
 * @returns {string} Display-friendly feature code
 */
export const getFeatureDisplayCode = (code) => {
  const { featureCode } = parseFeatureCode(code);
  return featureCode;
};

/**
 * Checks if a feature should be unchecked by default
 * Features with ::! pseudo-parent are enabled but not auto-checked
 * @param {string} code - Feature code
 * @returns {boolean} True if feature should be unchecked by default
 */
export const isDefaultUnchecked = (code) => {
  const { parentCode } = parseFeatureCode(code);
  return parentCode === DEFAULT_UNCHECKED_MARKER;
};

/**
 * Checks if a feature is independent (has no real parent dependency)
 * A feature is independent if it has no ::, or only has ::! (default unchecked marker)
 * @param {string} code - Feature code
 * @returns {boolean} True if feature has no parent dependency
 */
export const isIndependentFeature = (code) => {
  const { parentCode } = parseFeatureCode(code);
  return parentCode === null || parentCode === DEFAULT_UNCHECKED_MARKER;
};

/**
 * Finds a module by its feature code in a list of modules
 * @param {Array} modulesList - List of modules to search
 * @param {string} targetCode - The feature code to find (without @parent)
 * @returns {Object|null} The found module or null
 */
export const findModuleByCode = (modulesList, targetCode) => {
  if (!modulesList || !targetCode) return null;

  // Parse the target code to get just the feature part
  const { featureCode: searchCode } = parseFeatureCode(targetCode);

  // Collect all matches (there may be duplicates due to backend bugs)
  const matches = [];

  for (const module of modulesList) {
    // Check if this module's code matches (considering hierarchical format)
    const { featureCode } = parseFeatureCode(module.code);
    if (featureCode === searchCode) {
      matches.push(module);
    }

    // Also check subModules
    if (module.subModules) {
      for (const subModule of module.subModules) {
        const { featureCode: subFeatureCode } = parseFeatureCode(subModule.code);
        if (subFeatureCode === searchCode) {
          matches.push(subModule);
        }
      }
    }
  }

  if (matches.length === 0) {
    return null;
  }

  // If there are multiple matches (due to backend duplicates), prefer the checked one
  const checkedMatch = matches.find(m => m.checked === true);
  return checkedMatch || matches[0];
};

/**
 * Checks if a module's parent dependency is satisfied
 * @param {Object} module - The module to check
 * @param {Array} modulesList - All modules in the list
 * @returns {boolean} True if parent is checked or no parent dependency exists
 */
export const isParentEnabled = (module, modulesList) => {
  if (!module || !module.code) return true;

  const { featureCode, parentCode } = parseFeatureCode(module.code);

  // No parent dependency, always enabled
  if (!parentCode) return true;

  // ::! marker means "default unchecked" but still independent (no parent dependency)
  if (parentCode === DEFAULT_UNCHECKED_MARKER) return true;

  // Find the parent module
  const parentModule = findModuleByCode(modulesList, parentCode);

  // If parent not found, allow enabling (fail open)
  if (!parentModule) return true;

  return parentModule.checked === true;
};

/**
 * Gets all child modules that depend on a given parent module
 * @param {Object} parentModule - The parent module
 * @param {Array} modulesList - All modules in the list
 * @returns {Array} List of modules that depend on the parent
 */
export const getChildModules = (parentModule, modulesList) => {
  if (!parentModule || !modulesList) return [];

  const { featureCode: parentFeatureCode } = parseFeatureCode(parentModule.code);
  const children = [];

  for (const module of modulesList) {
    const { parentCode } = parseFeatureCode(module.code);
    if (parentCode === parentFeatureCode) {
      children.push(module);
    }

    // Also check subModules
    if (module.subModules) {
      for (const subModule of module.subModules) {
        const { parentCode: subParentCode } = parseFeatureCode(subModule.code);
        if (subParentCode === parentFeatureCode) {
          children.push(subModule);
        }
      }
    }
  }

  return children;
};

/**
 * Disables all child modules when a parent is disabled
 * This mutates the modulesList in place
 * @param {Object} parentModule - The parent module being disabled
 * @param {Array} modulesList - All modules in the list (will be mutated)
 */
export const disableChildModules = (parentModule, modulesList) => {
  const children = getChildModules(parentModule, modulesList);

  children.forEach(child => {
    child.checked = false;
    // Recursively disable grandchildren
    disableChildModules(child, modulesList);
  });
};

/**
 * Builds a dependency map for quick lookups
 * @param {Array} modulesList - All modules in the list
 * @returns {Map} Map of featureCode -> { module, parent, children }
 */
export const buildDependencyMap = (modulesList) => {
  const map = new Map();

  if (!modulesList) return map;

  // First pass: register all modules
  const allModules = [];
  modulesList.forEach(module => {
    allModules.push(module);
    if (module.subModules) {
      allModules.push(...module.subModules);
    }
  });

  // Second pass: build dependency relationships
  allModules.forEach(module => {
    const { featureCode, parentCode } = parseFeatureCode(module.code);

    map.set(featureCode, {
      module,
      parentCode,
      children: [],
    });
  });

  // Third pass: link children to parents
  map.forEach((value, key) => {
    if (value.parentCode && map.has(value.parentCode)) {
      map.get(value.parentCode).children.push(key);
    }
  });

  return map;
};

/**
 * Validates the hierarchical structure of modules
 * @param {Array} modulesList - All modules to validate
 * @returns {{ valid: boolean, errors: string[] }} Validation result
 */
export const validateHierarchy = (modulesList) => {
  const errors = [];

  if (!modulesList) return { valid: true, errors };

  const allModules = [];
  modulesList.forEach(module => {
    allModules.push(module);
    if (module.subModules) {
      allModules.push(...module.subModules);
    }
  });

  const featureCodes = new Set();

  allModules.forEach(module => {
    const { featureCode, parentCode } = parseFeatureCode(module.code);

    // Check for duplicate feature codes
    if (featureCodes.has(featureCode)) {
      errors.push(`Duplicate feature code: ${featureCode}`);
    }
    featureCodes.add(featureCode);

    // Check for circular dependencies (parent references itself)
    if (parentCode === featureCode) {
      errors.push(`Circular dependency: ${module.code} references itself`);
    }
  });

  // Check for missing parent references
  allModules.forEach(module => {
    const { parentCode } = parseFeatureCode(module.code);
    if (parentCode && !featureCodes.has(parentCode)) {
      errors.push(`Missing parent: ${module.code} references non-existent parent "${parentCode}"`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
